import { useEffect, useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../../contexts/LanguageContext';
import Dialog from '../../../../../components/Dialog';
import { ActionButton } from '../../../../../components/Button';
import { Box, Grid } from '@mui/material';
import SelectField from '../../../../../components/SelectField';
import DefaultConstants from '../../../../../data/Constants';
import { GovernanceStatusData, GovernanceStatusEnum, OccupancyStatusData, OccupancyStatusEnum, PartnerPmsSortByData, PartnerPmsSortByEnum, RoomStatusData, RoomStatusEnum } from '../../../../../data/Enums';
import Switch from '../../../../../components/Switch';
import { IDefaultDialogFilterProps } from '../../../../../interfaces/IReport';
import { IBlock, IFloor, IRoomType } from '../../../../../interfaces/IPartnerPms';
import { useAuthContext } from '../../../../../contexts/AuthContext';
import IEnterprise from '../../../../../interfaces/IEnterprise';
import useState from '../../../../../hooks/useState';


export interface IRoomOverviewReportFilters {
    enterprise: IEnterprise | undefined;
    occupancyStatus: OccupancyStatusEnum[];
    governanceStatus: GovernanceStatusEnum[];
    roomStatus: RoomStatusEnum[];
    block: IBlock[];
    floor: IFloor[];
    roomType: IRoomType[];
    onlyUserRooms: boolean;
    sortBy: PartnerPmsSortByEnum;
}

const RoomOverviewFilters: React.FunctionComponent<IDefaultDialogFilterProps> = ({
    open, 
    handleClose,
    handleOpenPreview,
    enterpriseList
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();

    const state = useState<IRoomOverviewReportFilters>({
        enterprise: enterprise,
        occupancyStatus: [],
        governanceStatus: [],
        roomStatus: [],
        block: [],
        floor: [],
        roomType: [],
        onlyUserRooms: false,
        sortBy: PartnerPmsSortByEnum.Description
    });


    useEffect(() => {
        if (open) {
            state.reset();
        }
        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        state.update('enterprise', state.values.enterprise);
        // eslint-disable-next-line
    }, [state.values.enterprise]);


    const enterprisePartnerPms = useMemo(() => {
        return {
            blocks: state.values.enterprise?.partnerPms.blocks ?? [],
            floors: state.values.enterprise?.partnerPms.floors ?? [],
            roomTypes: state.values.enterprise?.partnerPms.roomTypes ?? [],
            hasUserRooms: (state.values.enterprise?.partnerPms.userRooms ?? []).length > 0
        }
    }, [state.values.enterprise]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />,
            <ActionButton action='view' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleOpenPreview(state.values);
        // eslint-disable-next-line
    }, [state.values]);


    return (
        <Dialog 
            open={open}
            handleClose={handleClose}
            title={translate('roomOverview')}
            fullWidth={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
            maxWidth='md'
            fullHeight={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectField
                        label={translate('enterprise')}
                        name='enterprise'
                        value={state.values.enterprise?.id ?? 0}
                        onChange={e => {
                            const { name, value } = e.target;
                            const enterprise = enterpriseList.find(e => +e.id === +value);
                            state.update(name, enterprise);
                        }}
                        required={true}
                        data={enterpriseList.map(e => {
                            return {
                                value: +e.id,
                                text: e.name
                            };
                        })}
                    />
                </Grid>

                <Grid item xs={12} />

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('occupancyStatus')}
                        name='occupancyStatus'
                        value={state.values.occupancyStatus.length > 0 ? state.values.occupancyStatus : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const occupancyStatus = state.values.occupancyStatus.length > 0 && values.includes(-1) ? [] : Object.values(OccupancyStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, occupancyStatus);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(OccupancyStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('governanceStatus')}
                        name='governanceStatus'
                        value={state.values.governanceStatus.length > 0 ? state.values.governanceStatus : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const governanceStatus = state.values.governanceStatus.length > 0 && values.includes(-1) ? [] : Object.values(GovernanceStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, governanceStatus);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(GovernanceStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('roomStatus')}
                        name='roomStatus'
                        value={state.values.roomStatus.length > 0 ? state.values.roomStatus : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomStatus = state.values.roomStatus.length > 0 && values.includes(-1) ? [] : Object.values(RoomStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, roomStatus);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(RoomStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('block')}
                        name='block'
                        value={state.values.block.length > 0 ? state.values.block.map(b => +b.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const block = state.values.block.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.blocks.filter(b => values.includes(+b.id));
                            state.update(name, block);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.blocks.map(b => {
                            return {
                                value: +b.id,
                                text: b.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('floor')}
                        name='floor'
                        value={state.values.floor.length > 0 ? state.values.floor.map(f => +f.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const floor = state.values.floor.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.floors.filter(f => values.includes(+f.id));
                            state.update(name, floor);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.floors.map(f => {
                            return {
                                value: +f.id,
                                text: f.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('roomType')}
                        name='roomType'
                        value={state.values.roomType.length > 0 ? state.values.roomType.map(r => +r.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomType = state.values.roomType.length > 0 && values.includes(-1) ? [] : enterprisePartnerPms.roomTypes.filter(r => values.includes(+r.id));
                            state.update(name, roomType);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(enterprisePartnerPms.roomTypes.map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('sortBy')}
                        name='sortBy'
                        value={state.values.sortBy}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={Object.values(PartnerPmsSortByData).map(row => {
                            return { value: row.key, text: translate(row.name) };
                        })}
                    />
                </Grid>

                {
                    enterprisePartnerPms.hasUserRooms && <Grid item xs={12}>
                        <Box padding={2}>
                            <Switch
                                label={translate('_showOnlyRoomsAssignedToMe')}
                                labelPlacement='end'
                                name='onlyUserRooms'
                                value={state.values.onlyUserRooms ? 0 : 1}
                                checked={state.values.onlyUserRooms}
                                onChange={e => state.update(e.target.name, Boolean(+e.target.value))}
                            />
                        </Box>
                    </Grid>
                }
            </Grid>
        </Dialog>
    );
}

export default RoomOverviewFilters;